import React from "react";
import { Link } from "react-router-dom";
import PublicShell from "../../components/PublicShell";
import { ShieldCheck, Calendar, CreditCard, FileText, ClipboardCheck, LogOut, HeadphonesIcon, MessageSquare, Lock, ArrowRight, ChevronDown } from "lucide-react";
import { useState } from "react";
import usePageSeo from "../../lib/seo";

const TIMELINE = [
  { icon: Calendar, title: "Reserva confirmada", text: "Sua reserva é registrada na plataforma e recebe um código exclusivo de identificação." },
  { icon: CreditCard, title: "Pagamento seguro", text: "O pagamento é processado pelo Mercado Pago em ambiente seguro, com opção de Pix ou cartão de crédito." },
  { icon: FileText, title: "Contrato digital", text: "O contrato é gerado e vinculado à reserva, permitindo assinatura e consulta durante o período da hospedagem." },
  { icon: ClipboardCheck, title: "Check-in digital", text: "Antes da entrada, o checklist eletrônico registra as condições gerais do imóvel e promove mais transparência para ambas as partes." },
  { icon: ShieldCheck, title: "Hospedagem acompanhada", text: "Durante a hospedagem, a reserva permanece registrada e pode ser acompanhada pela equipe Azuhomes quando necessário." },
  { icon: LogOut, title: "Check-out digital", text: "Ao final da estadia, o checklist de saída registra as condições gerais do imóvel e conclui a jornada da reserva." },
];

const DIFF_CARDS = [
  { icon: CreditCard, title: "Pagamento processado pelo Mercado Pago", text: "Pix ou cartão de crédito, com confirmação automática após aprovação." },
  { icon: FileText, title: "Contrato digital vinculado à reserva", text: "Dados do imóvel, período e regras da locação disponíveis para assinatura eletrônica." },
  { icon: ClipboardCheck, title: "Check-in eletrônico", text: "Checklist digital registra as condições do imóvel antes da entrada do hóspede." },
  { icon: LogOut, title: "Check-out eletrônico", text: "Registro das condições ao final da estadia, encerrando a jornada da reserva." },
  { icon: FileText, title: "Histórico operacional", text: "Cada evento da reserva é registrado internamente para consulta futura." },
  { icon: HeadphonesIcon, title: "Atendimento da equipe Azuhomes", text: "Suporte pelos canais oficiais durante toda a jornada." },
];

const FAQ = [
  ["Quais são as formas de pagamento?", "Pix e cartão de crédito, processados pelo Mercado Pago."],
  ["A Azuhomes armazena os dados do meu cartão?", "Não. Os dados financeiros são processados pelo Mercado Pago."],
  ["Quando a reserva é confirmada?", "Após a aprovação do pagamento e validações necessárias."],
  ["Receberei um contrato?", "Sim, conforme o fluxo da reserva, o contrato digital é disponibilizado para assinatura e consulta."],
  ["Como funciona o check-in?", "Antes da entrada, o hóspede recebe as orientações e o checklist eletrônico."],
  ["Como funciona o check-out?", "No encerramento da hospedagem, o checklist de saída registra as condições gerais do imóvel."],
  ["Com quem falo em caso de dúvida?", "Com a equipe Azuhomes pelos canais oficiais apresentados no site."],
];

export default function ReservaSegura() {
  usePageSeo({
    title: "Reserva Segura",
    description: "Como funciona a jornada da reserva na Azuhomes: pagamento seguro, contrato digital, check-in e check-out eletrônicos.",
    path: "/reserva-segura",
  });
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <PublicShell>
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #F7F4EF 0%, #E9F5F3 60%)" }} data-testid="reserva-segura-page">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <span className="inline-block text-[11px] uppercase tracking-[0.32em] text-[#066B63] font-semibold mb-6">Reserva Segura Azuhomes</span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#22302E] leading-[1.1]">
            Na Azuhomes, segurança não acontece <span className="text-[#0AA89C]">apenas no pagamento</span>.
            <br />Ela acompanha toda a sua reserva.
          </h1>
          <p className="mt-6 text-lg text-[#22302E]/80 max-w-2xl leading-relaxed">
            Da confirmação ao check-out, cada etapa é registrada, documentada e acompanhada para oferecer mais tranquilidade ao hóspede e ao proprietário.
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 px-6 lg:px-10" data-testid="rs-timeline">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl text-[#22302E] text-center mb-12">A jornada da sua reserva</h2>
          <ol className="relative border-l-2 border-[#E9F5F3] ml-3">
            {TIMELINE.map((step, i) => {
              const Icon = step.icon;
              return (
                <li key={step.title} className="mb-10 ml-6" data-testid={`rs-step-${i + 1}`}>
                  <span className="absolute -left-[17px] flex items-center justify-center w-8 h-8 rounded-full bg-[#0AA89C] text-white ring-4 ring-white">
                    <Icon className="w-4 h-4" />
                  </span>
                  <h3 className="font-medium text-lg text-[#22302E]">{i + 1}. {step.title}</h3>
                  <p className="text-[#22302E]/70 mt-1 leading-relaxed">{step.text}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-16 px-6 lg:px-10 bg-[#F7F4EF]/50" data-testid="rs-diferenciais">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl text-[#22302E] text-center mb-12">Diferenciais da Reserva Segura Azuhomes</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DIFF_CARDS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-white border border-[#E9F5F3] rounded-lg p-6">
                <Icon className="w-6 h-6 text-[#0AA89C] mb-3" />
                <h3 className="font-medium text-[#22302E]">{title}</h3>
                <p className="text-sm text-[#22302E]/70 mt-1">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Como protegemos */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl text-[#22302E] mb-4">Como protegemos sua reserva</h2>
          <p className="text-[#22302E]/80 mb-8 leading-relaxed">
            A Azuhomes registra digitalmente as principais etapas da hospedagem, oferecendo mais organização, rastreabilidade e transparência para hóspedes e proprietários.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              ["Organização", "Cada reserva possui informações centralizadas."],
              ["Rastreabilidade", "As principais etapas são registradas no sistema."],
              ["Transparência", "Hóspede e operação possuem registros da jornada."],
              ["Atendimento", "A equipe Azuhomes acompanha o processo de hospedagem."],
            ].map(([title, desc]) => (
              <div key={title} className="border-l-2 border-[#0AA89C] pl-4">
                <div className="font-medium text-[#22302E]">{title}</div>
                <div className="text-sm text-[#22302E]/70 mt-1">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pagamento / Contrato / Check-in / Proteção */}
      <section className="py-16 px-6 lg:px-10 bg-[#F7F4EF]/50">
        <div className="max-w-4xl mx-auto space-y-10">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-[#22302E] mb-3">Pagamento pela Azuhomes</h2>
            <ul className="text-[#22302E]/80 space-y-1 list-disc pl-5 text-sm">
              <li>Aceitamos Pix e cartão de crédito.</li>
              <li>O pagamento é processado pelo Mercado Pago em ambiente seguro.</li>
              <li>A confirmação ocorre após aprovação da instituição financeira.</li>
              <li>A Azuhomes não armazena dados completos do cartão.</li>
              <li>O status da reserva é atualizado automaticamente ao receber a confirmação.</li>
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-[#22302E] mb-3">Como funciona o contrato</h2>
            <p className="text-[#22302E]/80 text-sm leading-relaxed">
              O contrato é preparado e disponibilizado digitalmente conforme o andamento da reserva. Contém dados do imóvel, período da hospedagem, dados das partes e regras da locação, com assinatura eletrônica e consulta permanente para o hóspede.
            </p>
          </div>
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-[#22302E] mb-3">Check-in e Check-out digitais</h2>
            <p className="text-[#22302E]/80 text-sm leading-relaxed">
              O envio do checklist eletrônico ocorre nos momentos apropriados antes da entrada e após a saída. O hóspede preenche pelo celular, com assinatura quando aplicável. O documento fica arquivado como registro da jornada.
            </p>
          </div>
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-[#22302E] mb-3">Proteção de dados</h2>
            <ul className="text-[#22302E]/80 space-y-1 list-disc pl-5 text-sm">
              <li>Uso de HTTPS em todas as páginas.</li>
              <li>Dados tratados conforme nossa <Link to="/politica-privacidade" className="text-[#0AA89C] underline">Política de Privacidade</Link>.</li>
              <li>Acesso restrito a áreas administrativas.</li>
              <li>Dados financeiros processados pelo Mercado Pago.</li>
              <li>Dados completos de cartão não são armazenados pela Azuhomes.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 lg:px-10" data-testid="rs-faq">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl text-[#22302E] mb-8 text-center">Perguntas frequentes</h2>
          <div className="space-y-2">
            {FAQ.map(([q, a], i) => (
              <div key={q} className="border border-[#E9F5F3] rounded-lg bg-white overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-medium text-[#22302E]">{q}</span>
                  <ChevronDown className={`w-4 h-4 text-[#0AA89C] transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-[#22302E]/70">{a}</div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/casas" className="inline-flex items-center gap-1 text-[#0AA89C] hover:text-[#066B63]">
              Ver casas disponíveis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
